/**
 * DecisionFlow — RoleAssignmentWizard
 * ui/src/components/admin/RoleAssignmentWizard.jsx
 * S620
 *
 * Dual-path role assignment modal.
 * Tab 1: Assign by Position (shuttle UI)
 * Tab 2: Assign by CSV (native FileReader + client-side validation)
 *
 * Spec authority: crl_spec_v2_0.md §12 + CCM spec §9 (Branch B wiring)
 * Endpoint: POST /api/v1/positions/bulk-role-assign (207 Multi-Status)
 *
 * MANDATORY: createPortal(…, document.body) at z-[9999] — modal pattern rule.
 * Width: 800px. Height: 600px scrollable content area. Fixed header + footer.
 *
 * Branch B (CCM §9): positions with active plan_assignment route through
 * role-transition workflow (POST /positions/role-transition). Positions
 * without active assignment use direct bulk-role-assign.
 * Active assignment detection: conservative amber warning if any selected
 * position has non-null role_id (§12.5 spec decision).
 *
 * CSS: rgb(var(--color-*)) tokens only. No <form> tags.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  X, Search, ChevronRight, ChevronLeft, Loader2,
  AlertTriangle, CheckCircle, RotateCcw, Upload, Download,
} from "lucide-react";
const API = "/api/v1";

// Native CSV parser — matches ImportDataPanel.parseCsvFile pattern (no external dependency)
async function parseCsvFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (!lines.length) throw new Error("File appears to be empty.");
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
  const rows = lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? ""]));
  });
  return { headers, rows, dataRowCount: rows.length };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    const msg  = body?.detail?.error?.message ?? body?.detail ?? `HTTP ${r.status}`;
    throw Object.assign(new Error(msg), { status: r.status });
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SkeletonRow() {
  return (
    <div className="flex items-center gap-2 px-3 py-2 animate-pulse">
      <div className="h-3 w-3/4 rounded bg-[rgb(var(--color-surface-muted))]" />
    </div>
  );
}

// Shuttle pane — left (available) or right (selected)
function ShuttlePane({ title, positions, search, onSearch, onToggle, selectedIds, filters, onFilterChange, emptyText }) {
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return positions.filter(p =>
      (!q || p.full_name?.toLowerCase().includes(q) || p.employee_id?.toLowerCase().includes(q)) &&
      (!filters.segment || p.segment_id === filters.segment) &&
      (!filters.bu || p.business_unit_id === filters.bu)
    );
  }, [positions, search, filters]);

  const segments = useMemo(() => {
    const s = new Map();
    positions.forEach(p => { if (p.segment_id) s.set(p.segment_id, p.segment_code ?? p.segment_id); });
    return [...s.entries()];
  }, [positions]);

  return (
    <div className="flex flex-col flex-1 min-w-0 rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
      <div className="bg-[rgb(var(--color-surface-muted))] px-3 py-2 border-b border-[rgb(var(--color-border))]">
        <span className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">
          {title} ({filtered.length})
        </span>
      </div>
      <div className="p-2 border-b border-[rgb(var(--color-border))] space-y-1.5">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[rgb(var(--color-text-muted))]" />
          <input
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-6 pr-2 py-1 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none"
            placeholder="Search…"
            value={search}
            onChange={e => onSearch(e.target.value)}
          />
        </div>
        {onFilterChange && segments.length > 0 && (
          <select
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none"
            value={filters.segment ?? ""}
            onChange={e => onFilterChange({ ...filters, segment: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">All Segments</option>
            {segments.map(([id, code]) => <option key={id} value={id}>{code}</option>)}
          </select>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-tok-xs text-[rgb(var(--color-text-muted))]">{emptyText}</p>
        ) : (
          filtered.map(p => {
            const checked = selectedIds.has(p.position_id);
            return (
              <label
                key={p.position_id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-[rgb(var(--color-surface-muted))] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(p.position_id)}
                  className="h-3.5 w-3.5 rounded border border-[rgb(var(--color-border))]"
                />
                <div className="min-w-0">
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text))] truncate">{p.full_name}</p>
                  <p className="text-[10px] text-[rgb(var(--color-text-muted))] truncate">
                    {[p.role_code, p.segment_code, p.territory_code].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Assign by Position (shuttle UI)
// ---------------------------------------------------------------------------
function AssignByPositionTab({ role, positions, loadingPositions }) {
  const [availSearch, setAvailSearch] = useState("");
  const [selSearch, setSelSearch]     = useState("");
  const [checkedAvail, setCheckedAvail] = useState(new Set());
  const [checkedSel, setCheckedSel]   = useState(new Set());
  const [selected, setSelected]       = useState(new Set()); // position_ids in right pane
  const [filters, setFilters]         = useState({ segment: null, bu: null });

  // Available = active positions not already on this role
  const available = useMemo(() =>
    positions.filter(p => p.is_active && p.role_id !== role.role_id && !selected.has(p.position_id)),
    [positions, role.role_id, selected]
  );
  const selectedPositions = useMemo(() =>
    positions.filter(p => selected.has(p.position_id)),
    [positions, selected]
  );

  function moveRight() {
    setSelected(s => new Set([...s, ...checkedAvail]));
    setCheckedAvail(new Set());
  }
  function moveLeft() {
    setSelected(s => { const n = new Set(s); checkedSel.forEach(id => n.delete(id)); return n; });
    setCheckedSel(new Set());
  }
  function toggleAvail(id) { setCheckedAvail(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleSel(id)   { setCheckedSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  // Expose selected to parent via ref-like approach — parent reads window.__raw_wizard_selected
  useEffect(() => {
    window.__wizard_selected_position_ids = [...selected];
    window.__wizard_selected_positions = selectedPositions;
  }, [selected, selectedPositions]);

  if (loadingPositions) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex gap-3 flex-1 min-h-0 p-4">
        <ShuttlePane
          title="Available"
          positions={available}
          search={availSearch}
          onSearch={setAvailSearch}
          onToggle={toggleAvail}
          selectedIds={checkedAvail}
          filters={filters}
          onFilterChange={setFilters}
          emptyText="No available positions."
        />
        <div className="flex flex-col items-center justify-center gap-2 shrink-0">
          <button
            type="button"
            onClick={moveRight}
            disabled={checkedAvail.size === 0}
            className="rounded-lg border border-[rgb(var(--color-border))] p-1.5 disabled:opacity-30 hover:bg-[rgb(var(--color-surface-muted))]"
            title="Move to selected"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={moveLeft}
            disabled={checkedSel.size === 0}
            className="rounded-lg border border-[rgb(var(--color-border))] p-1.5 disabled:opacity-30 hover:bg-[rgb(var(--color-surface-muted))]"
            title="Remove from selected"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
        <ShuttlePane
          title="Selected"
          positions={selectedPositions}
          search={selSearch}
          onSearch={setSelSearch}
          onToggle={toggleSel}
          selectedIds={checkedSel}
          filters={{}}
          onFilterChange={null}
          emptyText="No positions selected."
        />
      </div>
      {available.length > 0 && (
        <p className="px-4 pb-2 text-[10px] text-[rgb(var(--color-text-muted))]">
          ℹ Showing positions not already assigned to {role.role_name}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Assign by CSV
// ---------------------------------------------------------------------------
function AssignByCSVTab({ role, positions }) {
  const [file, setFile]             = useState(null);
  const [parsed, setParsed]         = useState(null); // { rows, resolved, warnings }
  const [parsing, setParsing]       = useState(false);

  const empMap = useMemo(() =>
    Object.fromEntries(positions.filter(p => p.employee_id).map(p => [p.employee_id, p])),
    [positions]
  );

  async function handleFile(f) {
    if (!f) return;
    setFile(f);
    setParsing(true);
    try {
      const { headers, rows } = await parseCsvFile(f);
      if (!headers.includes("employee_id")) {
        setParsed({ rows: [], resolved: [], warnings: ["Missing required column: employee_id"], unresolvedCount: 0 });
        return;
      }
      const warnings = [];
      const resolved = [];
      rows.forEach((row, i) => {
        const empId = (row.employee_id ?? "").trim();
        if (!empId) { warnings.push(`Row ${i + 2}: missing employee_id`); return; }
        const pos = empMap[empId];
        if (!pos) { warnings.push(`Row ${i + 2}: employee_id "${empId}" not found`); return; }
        if (!pos.is_active) { warnings.push(`Row ${i + 2}: position "${empId}" is inactive`); return; }
        if (row.role_code && row.role_code.trim() && row.role_code.trim() !== role.role_code) {
          warnings.push(`Row ${i + 2}: role_code "${row.role_code}" does not match target role "${role.role_code}" (will proceed)`);
        }
        resolved.push(pos);
      });
      setParsed({ rows, resolved, warnings, unresolvedCount: rows.length - resolved.length });
    } catch (e) {
      setParsed({ rows: [], resolved: [], warnings: [e.message], unresolvedCount: 0 });
    } finally {
      setParsing(false);
    }
  }

  function handleDownloadTemplate() {
    const csv = "employee_id,role_code\nEMP001,${role.role_code}\nEMP002,${role.role_code}\n".replace(/\${role\.role_code}/g, role.role_code);
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "role_assignment_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  // Expose resolved positions to parent
  useEffect(() => {
    window.__wizard_selected_position_ids = (parsed?.resolved ?? []).map(p => p.position_id);
    window.__wizard_selected_positions = parsed?.resolved ?? [];
  }, [parsed]);

  return (
    <div className="p-4 space-y-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Download the template, fill in your position list, and upload to assign.
      </p>
      <button
        type="button"
        onClick={handleDownloadTemplate}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
      >
        <Download className="h-4 w-4" /> Download Template
      </button>
      <div className="text-tok-xs text-[rgb(var(--color-text-muted))] space-y-0.5">
        <p className="font-medium text-[rgb(var(--color-text))]">Template columns:</p>
        <p><code className="font-mono">employee_id</code> — required, used to resolve position</p>
        <p><code className="font-mono">role_code</code> — optional, must match target role or be blank</p>
      </div>

      {/* Drop zone */}
      <label className="block">
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-8 cursor-pointer hover:border-[rgb(var(--color-primary))]/50 transition-colors">
          <Upload className="h-8 w-8 text-[rgb(var(--color-text-muted))] mb-2" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            {file ? file.name : "Drag and drop CSV file here, or click to browse"}
          </p>
        </div>
        <input
          type="file"
          accept=".csv"
          className="sr-only"
          onChange={e => handleFile(e.target.files?.[0] ?? null)}
        />
      </label>

      {parsing && <div className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text-muted))]"><Loader2 className="h-4 w-4 animate-spin" /> Parsing…</div>}

      {parsed && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] p-3 space-y-1.5 text-tok-sm">
          <p className="flex items-center gap-1.5 text-[rgb(var(--color-success))]">
            <CheckCircle className="h-4 w-4" /> {parsed.rows.length} rows parsed
          </p>
          <p className="flex items-center gap-1.5 text-[rgb(var(--color-success))]">
            <CheckCircle className="h-4 w-4" /> {parsed.resolved.length} employee_ids resolved to active positions
          </p>
          {parsed.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-[rgb(var(--color-warning))]">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {w}
            </p>
          ))}
          {parsed.resolved.some(p => p.role_id && p.role_id !== role.role_id) && (
            <p className="flex items-center gap-1.5 text-[rgb(var(--color-warning))]">
              <AlertTriangle className="h-4 w-4" />
              {parsed.resolved.filter(p => p.role_id && p.role_id !== role.role_id).length} position(s) have an active plan assignment (informational)
            </p>
          )}
          {file && (
            <button
              type="button"
              onClick={() => { setFile(null); setParsed(null); }}
              className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-danger))]"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview Step
// ---------------------------------------------------------------------------
function PreviewStep({ role, selectedPositions, onBack, onConfirm, confirming }) {
  // Conservative amber warning: any selected position with non-null role_id (§12.5 spec)
  const withActiveRole = selectedPositions.filter(p => p.role_id != null && p.role_id !== role.role_id);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-3">
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Assign to Positions — Preview
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
            Role: {role.role_name} ({role.role_code}) · {selectedPositions.length} position{selectedPositions.length !== 1 ? "s" : ""} selected
          </p>
        </div>

        <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden mb-3">
          <table className="w-full border-collapse">
            <thead className="bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
              <tr>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Name</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Current Role</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">New Role</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Assignment</th>
              </tr>
            </thead>
            <tbody>
              {selectedPositions.slice(0, 10).map(p => {
                const hasActiveRole = p.role_id != null && p.role_id !== role.role_id;
                return (
                  <tr key={p.position_id} className="border-b border-[rgb(var(--color-border))] last:border-0">
                    <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text))]">{p.full_name}</td>
                    <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">{p.role_code ?? "—"}</td>
                    <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-primary))]">{role.role_code}</td>
                    <td className="px-3 py-2 text-tok-xs">
                      {hasActiveRole
                        ? <span className="text-[rgb(var(--color-warning))]">⚠ Active</span>
                        : <span className="text-[rgb(var(--color-text-muted))]">—</span>
                      }
                    </td>
                  </tr>
                );
              })}
              {selectedPositions.length > 10 && (
                <tr><td colSpan={4} className="px-3 py-2 text-center text-tok-xs text-[rgb(var(--color-text-muted))]">
                  + {selectedPositions.length - 10} more positions
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {withActiveRole.length > 0 && (
          <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/5 p-3 text-tok-xs text-[rgb(var(--color-warning))]">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>
                {withActiveRole.length} position{withActiveRole.length !== 1 ? "s have" : " has"} an active plan assignment for the current period.
                The role classification will be updated. If a plan reassignment is also needed,
                use Assign by Position in Compensation Policies after this step.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming || selectedPositions.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50"
        >
          {confirming ? <><Loader2 className="h-4 w-4 animate-spin" /> Assigning…</> : "Confirm & Assign"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result Step
// ---------------------------------------------------------------------------
function ResultStep({ role, result, onRetryFailed, onDone }) {
  const { positions_succeeded, positions_failed } = result;
  const allOk = positions_failed.length === 0;

  return (
    <div className="flex flex-col h-full items-center justify-center p-8 text-center">
      {allOk ? (
        <>
          <CheckCircle className="h-12 w-12 text-[rgb(var(--color-success))] mb-4" />
          <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-1">Assignment Complete</p>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            {positions_succeeded} position{positions_succeeded !== 1 ? "s" : ""} assigned to {role.role_name}.
          </p>
        </>
      ) : (
        <>
          <AlertTriangle className="h-12 w-12 text-[rgb(var(--color-warning))] mb-4" />
          <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-1">Assignment Partially Complete</p>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-3">
            {positions_succeeded} of {positions_succeeded + positions_failed.length} positions assigned to {role.role_name}.
          </p>
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 text-left w-full max-w-sm mb-4">
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))] mb-2">{positions_failed.length} positions failed:</p>
            {positions_failed.map((f, i) => (
              <p key={i} className="text-tok-xs text-[rgb(var(--color-danger))]">· {f.position_id ?? f.employee_id ?? "unknown"} — {f.error ?? f.reason ?? "error"}</p>
            ))}
          </div>
        </>
      )}

      <div className="flex items-center gap-3 mt-4">
        {!allOk && onRetryFailed && (
          <button
            type="button"
            onClick={onRetryFailed}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            <RotateCcw className="h-4 w-4" /> Retry Failed
          </button>
        )}
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root — RoleAssignmentWizard
// ---------------------------------------------------------------------------
export default function RoleAssignmentWizard({ role, onClose, onSuccess }) {
  const [activeTab, setActiveTab]     = useState("position"); // "position" | "csv"
  const [step, setStep]               = useState("select");   // "select" | "preview" | "result"
  const [positions, setPositions]     = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [confirming, setConfirming]   = useState(false);
  const [result, setResult]           = useState(null);
  const [previewPositions, setPreviewPositions] = useState([]);

  // Fetch all active positions on mount
  useEffect(() => {
    setLoadingPositions(true);
    apiFetch("/positions?is_active=true&limit=1000")
      .then(data => setPositions(data.data ?? data.positions ?? []))
      .catch(() => setPositions([]))
      .finally(() => setLoadingPositions(false));
  }, []);

  const handlePreview = useCallback(() => {
    // Read positions selected in the active tab from the shared window ref
    const selected = window.__wizard_selected_positions ?? [];
    if (selected.length === 0) return;
    setPreviewPositions(selected);
    setStep("preview");
  }, []);

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    const positionIds = previewPositions.map(p => p.position_id);
    try {
      const data = await apiFetch("/positions/bulk-role-assign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role_id: role.role_id, position_ids: positionIds }),
      });
      const succeeded = data.results?.filter(r => r.status === "success").length ?? positionIds.length;
      const failed    = data.results?.filter(r => r.status !== "success") ?? [];
      setResult({ positions_succeeded: succeeded, positions_failed: failed });
      setStep("result");
    } catch (e) {
      setResult({ positions_succeeded: 0, positions_failed: previewPositions.map(p => ({ position_id: p.position_id, error: e.message })) });
      setStep("result");
    } finally {
      setConfirming(false);
    }
  }, [previewPositions, role.role_id]);

  const handleRetryFailed = useCallback(() => {
    // Re-enter shuttle with only failed positions
    const failedIds = new Set(result.positions_failed.map(f => f.position_id));
    window.__wizard_selected_positions = positions.filter(p => failedIds.has(p.position_id));
    window.__wizard_selected_position_ids = [...failedIds];
    setStep("select");
    setResult(null);
  }, [result, positions]);

  const handleDone = useCallback(() => {
    onSuccess?.({ positions_succeeded: result?.positions_succeeded ?? 0, positions_failed: result?.positions_failed ?? [] });
    onClose();
  }, [result, onSuccess, onClose]);

  const canPreview = (window.__wizard_selected_position_ids?.length ?? 0) > 0;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div
        className="relative flex flex-col bg-[rgb(var(--color-surface))] rounded-2xl shadow-2xl border border-[rgb(var(--color-border))]"
        style={{ width: 800, height: 600 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <div>
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Assign to Positions</p>
            <p className="text-tok-base font-semibold text-[rgb(var(--color-text))]">{role.role_name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab bar — only shown on select step */}
        {step === "select" && (
          <div className="flex border-b border-[rgb(var(--color-border))] shrink-0">
            {[{ key: "position", label: "Assign by Position" }, { key: "csv", label: "Assign by CSV" }].map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2.5 text-tok-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.key
                    ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
                    : "border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Content — scrollable */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {step === "select" && activeTab === "position" && (
            <AssignByPositionTab role={role} positions={positions} loadingPositions={loadingPositions} />
          )}
          {step === "select" && activeTab === "csv" && (
            <AssignByCSVTab role={role} positions={positions} />
          )}
          {step === "preview" && (
            <PreviewStep
              role={role}
              selectedPositions={previewPositions}
              onBack={() => setStep("select")}
              onConfirm={handleConfirm}
              confirming={confirming}
            />
          )}
          {step === "result" && (
            <ResultStep
              role={role}
              result={result}
              onRetryFailed={result?.positions_failed?.length > 0 ? handleRetryFailed : null}
              onDone={handleDone}
            />
          )}
        </div>

        {/* Footer — only shown on select step */}
        {step === "select" && (
          <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-5 py-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={!canPreview}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50"
            >
              Preview →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
